import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { Loop } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { ModalsKeys, Nullable } from 'global';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Skeleton, Stack } from '@mui/material';
import { ListingForm } from 'components/shared/TableListing/types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import {
  useQuote,
  InvoiceDetailsListing,
  NetFinanceAmountListing,
  quoteMapper,
  Quote,
  QuoteChargeListingEntity,
  QuoteLineItemType,
} from 'modules/quote';
import { useInvoice } from 'modules/invoice';
import { Equipment, useEquipmentActions } from 'modules/equipment';

import { quoteAPI } from '../../../../quote/api';
import { TradeInSection } from './tradeInTable';
import { CustomerDepositForm } from './CustomerDepositForm';
import { StepFooterNav } from '../../components/StepFooterNav';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { dealBuilderApi, UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables } from '../../../api';

type EquipmentUpdateDTO = Parameters<ReturnType<typeof useEquipmentActions>['onUpdateEquipment']>[0];
type InvoiceUpdateInput = Parameters<ReturnType<typeof useInvoice>['handleUpdateInvoice']>[0];

const usePricingMutations = () => {
  const { activeEntity, setActiveEntity } = useDealBuilderContext();
  const { onUpdateEquipment } = useEquipmentActions();
  const { handleUpdateInvoice } = useInvoice();

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote,
    {
      refetchQueries: [quoteAPI.getQuoteCalcById],
      awaitRefetchQueries: true,
    }
  );

  const quoteTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const equipmentUpdateTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const invoiceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const updateQuote = useCallback(
    async (quoteId: string, input: Record<string, unknown>) => {
      if (!quoteId) return;
      clearTimeout(quoteTimerRef.current);
      quoteTimerRef.current = setTimeout(async () => {
        const result = await updateDealBuilderQuoteMutation({ variables: { quoteId, input } });
        const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;
        if (updatedQuote && activeEntity) {
          setActiveEntity({ ...activeEntity, quote: updatedQuote });
        }
      }, 1500);
    },
    [updateDealBuilderQuoteMutation, setActiveEntity]
  );

  const updateEquipments = useCallback(
    async (dtos: EquipmentUpdateDTO[]) => {
      clearTimeout(equipmentUpdateTimerRef.current);
      equipmentUpdateTimerRef.current = setTimeout(() => {
        dtos.forEach(dto => onUpdateEquipment(dto, { withNotification: false }));
      }, 1500);
    },
    [onUpdateEquipment]
  );

  const updateInvoice = useCallback(
    async (input: InvoiceUpdateInput) => {
      clearTimeout(invoiceTimerRef.current);
      invoiceTimerRef.current = setTimeout(() => {
        handleUpdateInvoice(input);
      }, 1500);
    },
    [handleUpdateInvoice]
  );

  return { updateQuote, updateEquipments, updateInvoice };
};

export const PricingStep = () => {
  const {
    sourceData: { vo },
    activeEntity,
    setActiveEntity,
    equipment: { selectedEquipmentItem },
    pricing: { priceCalc },
  } = useDealBuilderContext();

  const { handleApplyFeesAndTaxes, isApplyingFeesAndTaxes } = useInvoice();
  const { handleFetchQuoteById } = useQuote();
  const { updateQuote, updateEquipments, updateInvoice } = usePricingMutations();
  const { handleOpen, handleClose } = useModal(ModalsKeys.Confirmation);
  const chargesSubmitRef = useRef<{ submit: () => ListingForm<QuoteChargeListingEntity> } | null>(null);

  const vendorOpportunityId = vo?.vendorOpportunityId ?? '';
  const dynamicsVOId = vo?.dynamicsVendorOpportunityId ?? '';

  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);

  // Do not reinitialize the listing when quote data changes
  const stableQuote = useMemo(
    () => (activeEntity?.quote ?? null) as unknown as Nullable<Quote>,
    [activeEntity?.quote?.quoteId, isRefreshingQuote]
  );

  const { data: equipmentQueryData, loading: equipmentLoading } = useQuery<{ equipment: Equipment }, { id: string }>(
    dealBuilderApi.getEquipmentDetailsById,
    {
      skip: !selectedEquipmentItem?.equipment.id,
      variables: {
        id: selectedEquipmentItem?.equipment.id!,
      },
    }
  );

  const handleSubscribe = useCallback(
    async (fields: QuoteChargeListingEntity[]) => {
      const quoteDTO = quoteMapper.toQuoteVariables(fields);
      const invoiceDTO = quoteMapper.toInvoiceDTO(quoteDTO);

      // Trade-ins are persisted separately by TradeInSection; exclude the empty placeholder so it isn't overwritten
      const { equipmentTradeIns: _equipmentTradeIns, ...quoteInput } = quoteDTO;

      const equipmentDTOs = fields
        .filter(({ type }) => type === QuoteLineItemType.Equipment)
        .map(({ equipment }) => ({
          ...quoteMapper.toEquipmentSyncDTO(equipment!),
          vendorOpportunityId,
          equipmentId: equipment!.equipmentId,
          dynamicsVendorOpportunityId: dynamicsVOId,
        }));

      await Promise.all([
        updateEquipments(equipmentDTOs),
        updateQuote(stableQuote?.quoteId!, { ...quoteInput, vendorOpportunityId }),
        updateInvoice({ VOId: vendorOpportunityId, entityId: dynamicsVOId, fieldsObj: invoiceDTO }),
      ]).catch(console.log);
    },
    [updateQuote, stableQuote?.quoteId, vendorOpportunityId, updateEquipments, updateInvoice, dynamicsVOId]
  );

  const equipmentList = useMemo<Equipment[]>(() => {
    if (!equipmentQueryData?.equipment) return [];
    return [{ ...equipmentQueryData.equipment, vendorOpportunityId }];
  }, [equipmentQueryData?.equipment, vendorOpportunityId]);

  const handleReapplyTaxesAndFees = useCallback(() => {
    handleOpen({
      title: 'Confirmation',
      message:
        'Warning - all existing taxes, fees, charges, and discounts on this invoice and associated equipment will be removed and replaced. Continue?',
      onConfirm: async () => {
        handleClose();
        setIsRefreshingQuote(true);
        await handleApplyFeesAndTaxes(vendorOpportunityId);

        const quoteId = activeEntity?.quote?.quoteId;
        const refreshedQuote = quoteId ? await handleFetchQuoteById(quoteId) : null;
        if (refreshedQuote && activeEntity) {
          setActiveEntity({
            ...activeEntity,
            quote: refreshedQuote as unknown as typeof activeEntity.quote,
          });
        }
        setIsRefreshingQuote(false);
      },
    });
  }, [
    handleOpen,
    handleClose,
    handleApplyFeesAndTaxes,
    handleFetchQuoteById,
    vendorOpportunityId,
    activeEntity,
    setActiveEntity,
  ]);

  return (
    <CardContainer
      title="Pricing"
      subTitle="Edit pricing line items, add charges, and review pricing before building a payment structure"
    >
      <RenderWithFallback
        isLoading={equipmentLoading || isRefreshingQuote}
        fallBack={<Skeleton variant="rounded" width="100%" height={400} />}
      >
        <InvoiceDetailsListing
          quote={stableQuote}
          VOId={vendorOpportunityId}
          dynamicsVOId={dynamicsVOId}
          equipmentList={equipmentList}
          onSubscribe={handleSubscribe}
          chargesSubmitRef={chargesSubmitRef}
          actionPanelBottom={() => (
            <Stack direction="row" gap="1rem" sx={{ paddingBottom: '0.5rem', borderBottom: `1px solid ${grey[300]}` }}>
              <Button
                sx={{ ml: 'auto' }}
                startIcon={<Loop />}
                loading={isApplyingFeesAndTaxes}
                onClick={handleReapplyTaxesAndFees}
              >
                Reapply Taxes and Fees
              </Button>
            </Stack>
          )}
        />

        <CustomerDepositForm />

        <TradeInSection />

        <NetFinanceAmountListing netFinanceAmount={priceCalc?.netFinanceAmount ?? null} />
      </RenderWithFallback>

      <StepFooterNav />
    </CardContainer>
  );
};
